import { useEffect, useState } from "react";
import AuthUser from "./AuthUser";
import toast, { Toaster } from "react-hot-toast";

export default function ClassAssign() {
    const { http, user } = AuthUser();
    const [assignments, setAssignments] = useState([]);
    const [classes, setClasses] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [currentAssignment, setCurrentAssignment] = useState(null);
    
    const [formData, setFormData] = useState({
        name: "",
        class_ids: [],
        company_id: user.company_id
    });

    const [selectedClassIds, setSelectedClassIds] = useState([]);

    useEffect(() => {
        fetchAssignments();
        fetchClasses();
    }, []);

    const fetchAssignments = async () => {
        setIsLoading(true);
        try {
            const response = await http.get(`/class_master_assign?company_id=${user.company_id}`);
            if (response.data.status === 'success') {
                setAssignments(response.data.data);
            }
        } catch (error) {
            toast.error("Error fetching class assignments");
            console.error(error);
        } finally {
            setIsLoading(false);
        }
    };

    const fetchClasses = async () => {
        try {
            const response = await http.get(`/class?company_id=${user.company_id}`);
            const classesData = response.data.data || response.data;
            setClasses(Array.isArray(classesData) ? classesData : []);
        } catch (error) {
            toast.error("Error fetching classes");
            console.error(error);
        }
    };

    const openAddModal = () => {
        setIsEditMode(false);
        setCurrentAssignment(null);
        setFormData({
            name: "",
            class_ids: [],
            company_id: user.company_id
        });
        setSelectedClassIds([]);
        fetchClasses();
        setShowModal(true);
    };

    const openEditModal = (assignment) => {
        setIsEditMode(true);
        setCurrentAssignment(assignment);
        
        const classIds = assignment.classes.map(cls => cls.id);
        
        setFormData({
            name: assignment.name,
            class_ids: classIds,
            company_id: assignment.company_id
        });
        
        setSelectedClassIds(classIds);
        fetchClasses();
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        setIsEditMode(false);
        setCurrentAssignment(null);
        setFormData({
            name: "",
            class_ids: [],
            company_id: user.company_id
        });
        setSelectedClassIds([]);
    };

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const handleCheckboxChange = (classId) => {
        const isSelected = selectedClassIds.includes(classId);
        let updatedIds;
        
        if (isSelected) {
            updatedIds = selectedClassIds.filter(id => id !== classId);
        } else {
            updatedIds = [...selectedClassIds, classId];
        }
        
        setSelectedClassIds(updatedIds);
        setFormData({
            ...formData,
            class_ids: updatedIds
        });
    };

    const toggleSelectAll = () => {
        if (selectedClassIds.length === classes.length) {
            setSelectedClassIds([]);
            setFormData({ ...formData, class_ids: [] });
        } else {
            const allIds = classes.map(cls => cls.id);
            setSelectedClassIds(allIds);
            setFormData({ ...formData, class_ids: allIds });
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!formData.name || formData.class_ids.length === 0) {
            toast.error("Please fill all required fields");
            return;
        }

        try {
            if (isEditMode && currentAssignment) {
                // Update existing assignment
                const response = await http.put(`/class_master_assign/${currentAssignment.id}`, formData);
                if (response.data.status === 'success') {
                    toast.success("Class assignment updated successfully");
                    fetchAssignments();
                    closeModal();
                }
            } else {
                // Create new assignment
                const response = await http.post('/class_master_assign', formData);
                if (response.data.status === 'success') {
                    toast.success("Class assignment created successfully");
                    fetchAssignments();
                    closeModal();
                }
            }
        } catch (error) {
            toast.error(error.response?.data?.message || "Error saving class assignment");
            console.error(error);
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm("Are you sure you want to delete this class assignment?")) {
            try {
                const response = await http.delete(`/class_master_assign/${id}`);
                if (response.data.status === 'success') {
                    toast.success("Class assignment deleted successfully");
                    fetchAssignments();
                }
            } catch (error) {
                toast.error("Error deleting class assignment");
                console.error(error);
            }
        }
    };

    return (
        <div className="container-fluid mt-4">
            <Toaster position="top-right" />
            
            <div className="row mb-4">
                <div className="col-12">
                    <div className="card shadow-sm">
                        <div className="card-header bg-primary text-white d-flex justify-content-between align-items-center">
                            <h5 className="mb-0">Class Master Assign</h5>
                            <button className="btn btn-light btn-sm" onClick={openAddModal}>
                                <i className="fas fa-plus me-2"></i>Add New
                            </button>
                        </div>
                        <div className="card-body">
                            {isLoading ? (
                                <div className="text-center py-5">
                                    <div className="spinner-border text-primary" role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                </div>
                            ) : (
                                <div className="table-responsive">
                                    <table className="table table-hover">
                                        <thead className="table-dark">
                                            <tr>
                                                <th style={{ width: '5%' }}>No</th>
                                                <th style={{ width: '25%' }}>Name</th>
                                                <th style={{ width: '60%' }} className="text-white">Assigned Classes</th>
                                                <th style={{ width: '10%' }} className="text-center">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {assignments.length === 0 ? (
                                                <tr>
                                                    <td colSpan="4" className="text-center py-4 text-muted">
                                                        No class assignments found. Click "Add New" to create one.
                                                    </td>
                                                </tr>
                                            ) : (
                                                assignments.map((assignment, index) => (
                                                    <tr key={assignment.id}>
                                                        <td>{index + 1}</td>
                                                        <td>
                                                            <strong>{assignment.name}</strong>
                                                        </td>
                                                        <td>
                                                            <div className="d-flex flex-wrap gap-2">
                                                                {assignment.classes && assignment.classes.map(cls => (
                                                                    <span key={cls.id} className="badge bg-primary">
                                                                        {cls.class_name}
                                                                    </span>
                                                                ))}
                                                            </div>
                                                        </td>
                                                        <td className="text-center">
                                                            <button 
                                                                className="btn btn-sm btn-primary"
                                                                onClick={() => openEditModal(assignment)}
                                                            >
                                                                <i className="fas fa-edit"></i>
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Add/Edit Modal */}
            {showModal && (
                <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-lg">
                        <div className="modal-content">
                            <div className="modal-header bg-primary text-white">
                                <h5 className="modal-title">
                                    {isEditMode ? 'Edit Class Assignment' : 'Add New Class Assignment'}
                                </h5>
                                <button type="button" className="btn-close btn-close-white" onClick={closeModal}></button>
                            </div>
                            <form onSubmit={handleSubmit}>
                                <div className="modal-body">
                                    <div className="mb-3">
                                        <label className="form-label">
                                            <strong>Group Name</strong> <span className="text-danger">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            name="name"
                                            value={formData.name}
                                            onChange={handleChange}
                                            placeholder="Enter group name (e.g., Engineering Classes)"
                                            required
                                        />
                                    </div>

                                    <div className="mb-3">
                                        <label className="form-label">
                                            <strong>Select Classes</strong> <span className="text-danger">*</span>
                                        </label>
                                        {classes.length === 0 ? (
                                            <div className="alert alert-warning">
                                                <i className="fas fa-exclamation-triangle me-2"></i>
                                                No classes available. Please add classes first from Master → Class List
                                            </div>
                                        ) : (
                                            <div className="border rounded p-3" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                                                <div className="form-check mb-2 pb-2 border-bottom">
                                                    <input
                                                        type="checkbox"
                                                        className="form-check-input"
                                                        id="selectAll"
                                                        checked={selectedClassIds.length === classes.length && classes.length > 0}
                                                        onChange={toggleSelectAll}
                                                    />
                                                    <label className="form-check-label fw-bold" htmlFor="selectAll">
                                                        Select All ({classes.length} classes)
                                                    </label>
                                                </div>
                                                {classes.map(cls => (
                                                    <div key={cls.id} className="form-check mb-2">
                                                        <input
                                                            type="checkbox"
                                                            className="form-check-input"
                                                            id={`class-${cls.id}`}
                                                            checked={selectedClassIds.includes(cls.id)}
                                                            onChange={() => handleCheckboxChange(cls.id)}
                                                        />
                                                        <label className="form-check-label" htmlFor={`class-${cls.id}`}>
                                                            {cls.class_name}
                                                        </label>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                        <small className="text-muted">
                                            {selectedClassIds.length > 0 ? `${selectedClassIds.length} class(es) selected. ` : ''}
                                            Select multiple classes to group them under one name
                                        </small>
                                    </div>
                                </div>
                                <div className="modal-footer">
                                    <button type="button" className="btn btn-secondary" onClick={closeModal}>
                                        Cancel
                                    </button>
                                    <button type="submit" className="btn btn-primary">
                                        <i className="fas fa-save me-2"></i>
                                        {isEditMode ? 'Update' : 'Save'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

